import React, { useState, useEffect, useRef } from 'react';
import { COLORS, MUTED_PALETTE } from './constants';
import { playAmbientTone } from './audioUtils';

const VisualBreathing = ({ audioCtxRef, soundEnabled }) => {
  const canvasRef = useRef(null);
  const [phase, setPhase] = useState('inhale');
  const [breathCount, setBreathCount] = useState(0);
  const particlesRef = useRef([]);
  const scaleRef = useRef(0.5);
  const colorTransitionRef = useRef(0); // 0-1 transition between colors
  const currentColorIndexRef = useRef(0);
  const nextColorIndexRef = useRef(1);
  
  // Interpolate between two hex colors
  const lerpColor = (color1, color2, t) => {
    const r1 = parseInt(color1.slice(1, 3), 16);
    const g1 = parseInt(color1.slice(3, 5), 16);
    const b1 = parseInt(color1.slice(5, 7), 16);
    const r2 = parseInt(color2.slice(1, 3), 16);
    const g2 = parseInt(color2.slice(3, 5), 16);
    const b2 = parseInt(color2.slice(5, 7), 16);
    
    const r = Math.round(r1 + (r2 - r1) * t);
    const g = Math.round(g1 + (g2 - g1) * t);
    const b = Math.round(b1 + (b2 - b1) * t);
    
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  };
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // Use alpha: false to disable canvas transparency (prevents checkerboard)
    const ctx = canvas.getContext('2d', { alpha: false });
    canvas.width = canvas.offsetWidth * 2;
    canvas.height = canvas.offsetHeight * 2;
    
    // Initialize floating particles
    particlesRef.current = Array.from({ length: 50 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      size: 2 + Math.random() * 4,
      speed: 0.2 + Math.random() * 0.3,
      angle: Math.random() * Math.PI * 2,
      opacity: 0.2 + Math.random() * 0.4,
    }));
    
    let animationTime = 0;
    let cycleStart = Date.now();
    let localBreathCount = 0;
    
    const animate = () => {
      animationTime++;
      const elapsed = (Date.now() - cycleStart) / 1000;
      
      // Breath cycle: 4s inhale, 2s hold, 6s exhale = 12s total
      let currentPhase = 'inhale';
      let breathProgress = 0;
      
      if (elapsed < 4) {
        currentPhase = 'inhale';
        breathProgress = elapsed / 4;
        scaleRef.current = 0.5 + breathProgress * 0.5;
        // Gradually transition color during inhale
        colorTransitionRef.current = Math.min(1, colorTransitionRef.current + 0.002);
      } else if (elapsed < 6) {
        currentPhase = 'hold';
        scaleRef.current = 1;
      } else if (elapsed < 12) {
        currentPhase = 'exhale';
        breathProgress = (elapsed - 6) / 6;
        scaleRef.current = 1 - breathProgress * 0.5;
      } else {
        // New breath cycle
        cycleStart = Date.now();
        localBreathCount++;
        setBreathCount(localBreathCount);
        
        // Shift colors for next transition
        if (colorTransitionRef.current >= 1) {
          currentColorIndexRef.current = nextColorIndexRef.current;
          nextColorIndexRef.current = (nextColorIndexRef.current + 1) % MUTED_PALETTE.length;
          colorTransitionRef.current = 0;
        }
      }
      
      setPhase(currentPhase);
      
      // Get interpolated color
      const currentColor = lerpColor(
        MUTED_PALETTE[currentColorIndexRef.current],
        MUTED_PALETTE[nextColorIndexRef.current],
        colorTransitionRef.current
      );
      
      // Background gradient - subtle color tint
      const bgGradient = ctx.createRadialGradient(
        canvas.width / 2, canvas.height / 2, 0,
        canvas.width / 2, canvas.height / 2, canvas.width / 2
      );
      bgGradient.addColorStop(0, '#3D4852');
      bgGradient.addColorStop(1, '#2D3640');
      ctx.fillStyle = bgGradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const baseRadius = Math.min(canvas.width, canvas.height) * 0.25;
      const radius = baseRadius * scaleRef.current;
      
      // Outer glow rings (pulsing)
      for (let i = 4; i >= 0; i--) {
        const ringRadius = radius + i * 25 + Math.sin(animationTime * 0.015 + i) * 12;
        const ringOpacity = 0.12 - i * 0.02;
        
        ctx.beginPath();
        ctx.arc(centerX, centerY, ringRadius, 0, Math.PI * 2);
        ctx.fillStyle = currentColor + Math.floor(ringOpacity * 255).toString(16).padStart(2, '0');
        ctx.fill();
      }
      
      // Main breathing circle with gradient
      const circleGradient = ctx.createRadialGradient(
        centerX - radius * 0.2, centerY - radius * 0.2, 0,
        centerX, centerY, radius
      );
      circleGradient.addColorStop(0, currentColor);
      circleGradient.addColorStop(0.6, currentColor + 'DD');
      circleGradient.addColorStop(1, currentColor + '88');
      
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
      ctx.fillStyle = circleGradient;
      ctx.fill();
      
      // Inner shimmer (rotating highlight)
      const shimmerAngle = animationTime * 0.008;
      ctx.beginPath();
      ctx.arc(
        centerX + Math.cos(shimmerAngle) * radius * 0.3,
        centerY + Math.sin(shimmerAngle) * radius * 0.3,
        radius * 0.18,
        0, Math.PI * 2
      );
      ctx.fillStyle = 'rgba(255, 255, 255, 0.12)';
      ctx.fill();
      
      // Second smaller shimmer
      ctx.beginPath();
      ctx.arc(
        centerX + Math.cos(shimmerAngle + 2) * radius * 0.5,
        centerY + Math.sin(shimmerAngle + 2) * radius * 0.5,
        radius * 0.08,
        0, Math.PI * 2
      );
      ctx.fillStyle = 'rgba(255, 255, 255, 0.08)';
      ctx.fill();
      
      // Floating particles that respond to breathing
      particlesRef.current.forEach(p => {
        const dx = centerX - p.x;
        const dy = centerY - p.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        
        if (currentPhase === 'inhale') {
          p.x += (dx / dist) * p.speed * 2.5;
          p.y += (dy / dist) * p.speed * 2.5;
        } else if (currentPhase === 'exhale') {
          p.x -= (dx / dist) * p.speed * 1.8;
          p.y -= (dy / dist) * p.speed * 1.8;
        }
        
        // Gentle drift
        p.x += Math.cos(p.angle + animationTime * 0.008) * 0.4;
        p.y += Math.sin(p.angle + animationTime * 0.008) * 0.4;
        
        // Wrap around edges
        if (p.x < -20) p.x = canvas.width + 20;
        if (p.x > canvas.width + 20) p.x = -20;
        if (p.y < -20) p.y = canvas.height + 20;
        if (p.y > canvas.height + 20) p.y = -20;
        
        // Draw particle with current color tint
        const particleOpacity = p.opacity * (0.5 + Math.sin(animationTime * 0.03 + p.angle) * 0.3);
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${particleOpacity})`;
        ctx.fill();
      });
      
      requestAnimationFrame(animate);
    };
    
    const animationId = requestAnimationFrame(animate);
    
    // Sound
    let toneInterval;
    const audioCtx = audioCtxRef?.current;
    if (soundEnabled && audioCtx) {
      toneInterval = setInterval(() => {
        playAmbientTone(audioCtx, 220 + Math.random() * 110, 3, 0.05);
      }, 4000);
    }
    
    return () => {
      cancelAnimationFrame(animationId);
      if (toneInterval) clearInterval(toneInterval);
    };
  }, [audioCtxRef, soundEnabled]);
  
  return (
    <div className="flex flex-col items-center justify-center h-full gap-4">
      <canvas
        ref={canvasRef}
        className="rounded-2xl"
        style={{ width: '85vw', maxWidth: '800px', height: '55vh', maxHeight: '480px' }}
      />
      <div className="text-center">
        <p className="text-2xl font-light tracking-widest uppercase mb-2" style={{ color: COLORS.stone }}>
          {phase === 'inhale' && 'Breathe In...'}
          {phase === 'hold' && 'Hold...'}
          {phase === 'exhale' && 'Breathe Out...'}
        </p>
        <p className="text-sm text-white/40">Breath {breathCount + 1}</p>
      </div>
    </div>
  );
};

export default VisualBreathing;
